/**
 * Derived, memoised views over a HomeModel. Everything here is keyed on the
 * model object identity, so it recomputes only when the server sends a new
 * model (registry / mapping change), never on state diffs.
 */
import type { ClimateSystem, DeviceType, EntityMeta, Floor, HomeModel, Room } from '@home/shared';
import { pickBase } from './icons/resolve.js';
import { isBinEntity } from './bins.js';
import { LIGHT_GROUPS, LIGHT_SUBGROUP_MIN, lightGroupOf, naturalCompare, type LightGroupId } from './icons/lights.js';

export interface ModelIndex {
  byRoom: Map<string, EntityMeta[]>;
  /** Entities whose page also shows them in another room (EntityMeta.alsoRoomIds), by that room. Not in byRoom (counts, admin). */
  alsoByRoom: Map<string, EntityMeta[]>;
  unsorted: EntityMeta[];
  climateByRoom: Map<string, ClimateSystem[]>;
  /** Entity ids that appear in any climate system (sources/sensors). */
  climateEntityIds: Set<string>;
  floors: Array<{ floor: Floor | null; rooms: Room[] }>;
  rooms: Map<string, Room>;
}

const cache = new WeakMap<HomeModel, ModelIndex>();

export function indexModel(model: HomeModel): ModelIndex {
  const hit = cache.get(model);
  if (hit) return hit;
  const byRoom = new Map<string, EntityMeta[]>();
  const unsorted: EntityMeta[] = [];
  const alsoByRoom = new Map<string, EntityMeta[]>();
  for (const meta of Object.values(model.entities)) {
    for (const r of meta.alsoRoomIds ?? []) {
      if (r === meta.roomId || !model.rooms.some((x) => x.id === r)) continue;
      let list = alsoByRoom.get(r);
      if (!list) alsoByRoom.set(r, (list = []));
      list.push(meta);
    }
    if (meta.roomId && model.rooms.some((r) => r.id === meta.roomId)) {
      let list = byRoom.get(meta.roomId);
      if (!list) byRoom.set(meta.roomId, (list = []));
      list.push(meta);
    } else unsorted.push(meta);
  }
  for (const list of byRoom.values()) list.sort((a, b) => a.name.localeCompare(b.name));
  unsorted.sort((a, b) => a.domain.localeCompare(b.domain) || a.name.localeCompare(b.name));

  const climateByRoom = new Map<string, ClimateSystem[]>();
  const climateEntityIds = new Set<string>();
  for (const sys of model.climate) {
    for (const r of sys.roomIds) {
      let l = climateByRoom.get(r);
      if (!l) climateByRoom.set(r, (l = []));
      l.push(sys);
    }
    for (const s of sys.sources) climateEntityIds.add(s.entityId);
  }

  const rooms = new Map(model.rooms.map((r) => [r.id, r]));
  const sortedFloors = [...model.floors].sort((a, b) => a.order - b.order);
  const floors: ModelIndex['floors'] = sortedFloors.map((f) => ({
    floor: f,
    rooms: model.rooms.filter((r) => r.floorId === f.id).sort((a, b) => a.order - b.order),
  }));
  const loose = model.rooms
    .filter((r) => !r.floorId || !model.floors.some((f) => f.id === r.floorId))
    .sort((a, b) => a.order - b.order);
  if (loose.length) floors.push({ floor: null, rooms: loose });

  const idx: ModelIndex = {
    byRoom,
    alsoByRoom,
    unsorted,
    climateByRoom,
    climateEntityIds,
    floors: floors.filter((f) => f.rooms.length > 0),
    rooms,
  };
  cache.set(model, idx);
  return idx;
}

// ---------------------------------------------------------------- grouping

export type GroupId =
  | 'lights'
  | 'switches'
  | 'covers'
  | 'fans'
  | 'climate'
  | 'media'
  | 'security'
  | 'locks'
  | 'cameras'
  | 'sensors'
  | 'actions'
  | 'appliances'
  | 'controls'
  | 'weather'
  | 'people'
  | 'other'
  | 'settings'
  | 'diagnostics';

export interface Group {
  id: GroupId;
  title: string;
  icon: string;
  entities: EntityMeta[];
}

const GROUP_META: Record<GroupId, { title: string; icon: string; order: number }> = {
  lights: { title: 'Lights', icon: 'bulb', order: 1 },
  climate: { title: 'Climate', icon: 'thermostat', order: 2 },
  covers: { title: 'Blinds & covers', icon: 'blind', order: 3 },
  switches: { title: 'Switches', icon: 'power', order: 4 },
  fans: { title: 'Fans', icon: 'fan', order: 5 },
  media: { title: 'Media', icon: 'speaker', order: 6 },
  locks: { title: 'Doors & locks', icon: 'lock', order: 7 },
  security: { title: 'Security', icon: 'shield', order: 8 },
  cameras: { title: 'Cameras', icon: 'camera', order: 9 },
  actions: { title: 'Scenes & actions', icon: 'palette', order: 10 },
  appliances: { title: 'Appliances', icon: 'robot', order: 11 },
  sensors: { title: 'Sensors', icon: 'gauge', order: 12 },
  controls: { title: 'Controls', icon: 'sliders', order: 13 },
  weather: { title: 'Weather', icon: 'cloud-sun', order: 14 },
  people: { title: 'People', icon: 'users', order: 15 },
  other: { title: 'Other', icon: 'grid', order: 16 },
  settings: { title: 'Settings', icon: 'settings', order: 17 },
  diagnostics: { title: 'Diagnostics', icon: 'chip', order: 18 },
};

export function groupFor(meta: EntityMeta): GroupId {
  if (meta.category === 'diagnostic') return 'diagnostics';
  if (meta.category === 'config') return 'settings';
  switch (meta.domain) {
    case 'light':
      return 'lights';
    case 'switch':
    case 'input_boolean':
      // A relay that drives lights (e.g. switch.garage_lights) belongs with the lights.
      // So does a plug feeding a lamp (switch.living_room_side_lamp).
      if (meta.domain === 'switch' && meta.deviceClass !== 'outlet' && /\b(lights?|lamps?)\b/i.test(`${meta.name} ${meta.id.replace(/_/g, ' ')}`)) return 'lights';
      return meta.deviceClass === 'outlet' || meta.domain === 'switch' ? 'switches' : 'controls';
    case 'cover':
      return meta.deviceClass === 'door' || meta.deviceClass === 'garage' || meta.deviceClass === 'gate' ? 'locks' : 'covers';
    case 'fan':
      return 'fans';
    case 'climate':
    case 'humidifier':
    case 'water_heater':
      return 'climate';
    case 'media_player':
    case 'remote':
      return 'media';
    case 'lock':
      return 'locks';
    case 'alarm_control_panel':
    case 'siren':
      return 'security';
    case 'camera':
    case 'image':
      return 'cameras';
    case 'sensor':
    case 'binary_sensor':
      return 'sensors';
    case 'scene':
    case 'script':
    case 'button':
    case 'input_button':
      return 'actions';
    case 'vacuum':
    case 'valve':
    case 'lawn_mower':
    case 'update':
      return 'appliances';
    case 'input_number':
    case 'number':
    case 'input_select':
    case 'select':
    case 'input_text':
    case 'text':
    case 'input_datetime':
    case 'datetime':
    case 'date':
    case 'time':
    case 'schedule':
    case 'timer':
    case 'counter':
      return 'controls';
    case 'weather':
    case 'sun':
      return 'weather';
    case 'person':
    case 'device_tracker':
      return 'people';
    default:
      return 'other';
  }
}

const groupCache = new WeakMap<EntityMeta[], Group[]>();

/** Groups a room's entities for display; climate-system members are shown by the climate card instead. */
export function groupEntities(list: EntityMeta[], exclude?: Set<string>): Group[] {
  const hit = !exclude ? groupCache.get(list) : undefined;
  if (hit) return hit;
  const map = new Map<GroupId, EntityMeta[]>();
  for (const m of list) {
    if (exclude?.has(m.id)) continue;
    const g = groupFor(m);
    let l = map.get(g);
    if (!l) map.set(g, (l = []));
    l.push(m);
  }
  const groups = [...map.entries()]
    .map(([id, entities]) => ({ id, title: GROUP_META[id].title, icon: GROUP_META[id].icon, entities }))
    .sort((a, b) => GROUP_META[a.id].order - GROUP_META[b.id].order);
  if (!exclude) groupCache.set(list, groups);
  return groups;
}

export const groupTitle = (id: GroupId): string => GROUP_META[id].title;

// ---------------------------------------------------------------- roles (v2 declutter)

export type Role = NonNullable<EntityMeta['role']>;

const FALLBACK_SETTING = new Set(['input_boolean', 'input_number', 'input_select', 'input_text', 'input_datetime', 'input_button', 'number', 'select', 'text', 'time', 'date', 'datetime', 'schedule', 'automation', 'timer', 'counter', 'script']);

/** The server sets `role` (server/src/ha/device-types.ts); this fallback covers older servers and the mock. */
export function roleOf(meta: EntityMeta): Role {
  if (meta.role) return meta.role;
  if (meta.category === 'diagnostic') return 'diagnostic';
  if (meta.category === 'config' || FALLBACK_SETTING.has(meta.domain)) return 'setting';
  if (meta.domain === 'update' || meta.domain === 'event' || meta.domain === 'device_tracker') return 'diagnostic';
  if (meta.domain === 'sensor' || meta.domain === 'binary_sensor' || meta.domain === 'weather' || meta.domain === 'sun' || meta.domain === 'person' || meta.domain === 'image') return 'reading';
  if (meta.domain === 'scene') return 'setting';
  return 'control';
}

/** Readings that belong in a room's strip (mirrors the server's ROOM_READING_CLASSES). */
export type ReadingKind = 'temperature' | 'humidity' | 'occupancy' | 'co2' | 'illuminance' | 'opening' | 'leak' | 'smoke';
const READING_KIND: Record<string, ReadingKind> = {
  temperature: 'temperature', humidity: 'humidity', occupancy: 'occupancy', motion: 'occupancy', presence: 'occupancy',
  carbon_dioxide: 'co2', co2: 'co2', illuminance: 'illuminance', door: 'opening', window: 'opening', opening: 'opening',
  garage_door: 'opening', moisture: 'leak', smoke: 'smoke', gas: 'smoke', carbon_monoxide: 'smoke',
};
/** Camera object detection (Frigate car / person / all) watches outside the room it's filed under: never room occupancy. */
export const isCameraDetection = (m: EntityMeta): boolean => m.platform === 'frigate';
export const readingKind = (m: EntityMeta): ReadingKind | undefined => {
  if (!((m.domain === 'sensor' || m.domain === 'binary_sensor') && m.deviceClass)) return undefined;
  const kind = READING_KIND[m.deviceClass];
  return kind === 'occupancy' && isCameraDetection(m) ? undefined : kind;
};

export interface ControlGroup {
  id: 'lights' | 'climate' | 'covers' | 'media' | 'locks' | 'other';
  title: string;
  icon: string;
  entities: EntityMeta[];
}
const CONTROL_GROUPS: Record<ControlGroup['id'], { title: string; icon: string }> = {
  lights: { title: 'Lights', icon: 'bulb' },
  climate: { title: 'Climate', icon: 'thermostat' },
  covers: { title: 'Blinds & windows', icon: 'blind' },
  media: { title: 'Media', icon: 'speaker' },
  locks: { title: 'Doors & locks', icon: 'lock' },
  other: { title: 'Other', icon: 'grid' },
};
const controlGroupOf = (m: EntityMeta): ControlGroup['id'] => {
  const g = groupFor({ ...m, category: null });
  return g === 'lights' || g === 'climate' || g === 'covers' || g === 'media' || g === 'locks' ? g : 'other';
};

export interface RoomView {
  groups: ControlGroup[];
  /** Readings strip, one entry per kind (first entity is the primary for history). */
  readings: Array<{ kind: ReadingKind; ids: string[] }>;
  /** Tile count (controls only), for metrics. */
  tiles: number;
}

const roomViewCache = new WeakMap<HomeModel, Map<string, RoomView>>();
const READING_ORDER: ReadingKind[] = ['temperature', 'humidity', 'occupancy', 'co2', 'illuminance', 'opening', 'leak', 'smoke'];

/** Room page contents: controls grouped (lights first, no cameras), plus a readings strip deduped against the climate card. */
export function roomView(model: HomeModel, roomId: string): RoomView {
  let m = roomViewCache.get(model);
  if (!m) roomViewCache.set(model, (m = new Map()));
  const hit = m.get(roomId);
  if (hit) return hit;
  const idx = indexModel(model);
  const own = idx.byRoom.get(roomId) ?? [];
  const also = idx.alsoByRoom.get(roomId);
  const list = also?.length ? [...own, ...also].sort((a, b) => a.name.localeCompare(b.name)) : own;
  const systems = idx.climateByRoom.get(roomId) ?? [];
  const sources = new Set(systems.flatMap((s) => s.sources.map((x) => x.entityId)));
  const climateSensors = new Set(systems.flatMap((s) => [...s.sensors, ...(s.humidity ?? [])]));
  const binsOwned = model.modules.includes('bins');
  const groups = new Map<ControlGroup['id'], EntityMeta[]>();
  const readings = new Map<ReadingKind, string[]>();
  for (const e of list) {
    // Cameras live on the security dashboard only (owner: not on room pages).
    if (e.domain === 'camera') continue;
    // Peloton rider sensors are rendered by the room's Peloton card (screens/peloton), not as generic readings.
    if (e.platform === 'peloton') continue;
    // Bin collection entities live on the bins dashboard (lib/bins.ts) while the module is on.
    if (binsOwned && isBinEntity(e.id)) continue;
    const role = roleOf(e);
    if (role === 'control' && !sources.has(e.id) && e.domain !== 'scene') {
      const g = controlGroupOf(e);
      let l = groups.get(g);
      if (!l) groups.set(g, (l = []));
      l.push(e);
    } else if (role === 'reading' && !climateSensors.has(e.id)) {
      const k = readingKind(e);
      if (!k) continue;
      // The climate card already shows temperature and humidity.
      if (systems.length && (k === 'temperature' || k === 'humidity')) continue;
      let l = readings.get(k);
      if (!l) readings.set(k, (l = []));
      l.push(e.id);
    }
  }
  const order = Object.keys(CONTROL_GROUPS) as Array<ControlGroup['id']>;
  const v: RoomView = {
    groups: order.filter((g) => groups.has(g)).map((g) => ({ id: g, ...CONTROL_GROUPS[g], entities: groups.get(g)! })),
    readings: READING_ORDER.filter((k) => readings.has(k)).map((k) => ({ kind: k, ids: readings.get(k)! })),
    tiles: [...groups.values()].reduce((a, l) => a + l.length, 0),
  };
  m.set(roomId, v);
  return v;
}

// ---------------------------------------------------------------- device types

export const DEVICE_TYPE_LABEL: Record<DeviceType, { title: string; blurb: string }> = {
  thermostat: { title: 'Thermostats', blurb: 'Heating zones, boiler and hot water' },
  aircon: { title: 'Air conditioning', blurb: 'AC units, zones and the modulator' },
  light: { title: 'Lights', blurb: 'Dimmers, lamps and controllers' },
  cover: { title: 'Blinds & skylights', blurb: 'Blinds, shutters and skylights' },
  energy: { title: 'Inverters & batteries', blurb: 'Solar, batteries, meters and tariffs' },
  ev: { title: 'EV chargers', blurb: 'Chargers and their breakers' },
  vehicle: { title: 'Cars', blurb: 'Connected cars' },
  hottub: { title: 'Hot tub', blurb: 'Heater, pumps and chemistry' },
  security: { title: 'Security', blurb: 'Alarm, motion sensors and cameras' },
  door: { title: 'Door & intercom', blurb: 'Locks, latches and doorbells' },
  network: { title: 'Network', blurb: 'Wi-Fi, switches and internet' },
  media: { title: 'Media', blurb: 'Speakers and TVs' },
  sensor: { title: 'Sensors', blurb: 'Weather, leak and room sensors' },
  appliance: { title: 'Appliances', blurb: 'Washer, dryer, vacuums and more' },
  other: { title: 'Other devices', blurb: 'Plugs, tablets and integrations' },
  helper: { title: 'House helpers', blurb: 'Settings that are not tied to a device' },
};
export const deviceTypeTitle = (t: DeviceType | undefined): string => DEVICE_TYPE_LABEL[t ?? 'other'].title;

/** Device ids → their entities (memoised per model). */
const byDeviceCache = new WeakMap<HomeModel, Map<string, EntityMeta[]>>();
export function entitiesByDevice(model: HomeModel): Map<string, EntityMeta[]> {
  const hit = byDeviceCache.get(model);
  if (hit) return hit;
  const m = new Map<string, EntityMeta[]>();
  for (const e of Object.values(model.entities)) {
    if (!e.deviceId) continue;
    let l = m.get(e.deviceId);
    if (!l) m.set(e.deviceId, (l = []));
    l.push(e);
  }
  for (const l of m.values()) l.sort((a, b) => a.name.localeCompare(b.name));
  byDeviceCache.set(model, m);
  return m;
}

// ---------------------------------------------------------------- light sub-groups

export interface LightSubgroup {
  id: LightGroupId;
  title: string;
  icon: string;
  entities: EntityMeta[];
}

/** Display name inside a room ("Row 1" in the Master Bedroom), the sort key for light tiles. */
const lightLabel = (m: EntityMeta, roomName?: string): string => {
  if (m.shortName) return m.shortName;
  if (roomName && m.name.toLowerCase().startsWith(`${roomName.toLowerCase()} `)) return m.name.slice(roomName.length + 1);
  return m.name;
};

/** Lights in name order, numbers counted naturally ("Row 2" before "Row 10"). */
export function sortLights(list: EntityMeta[], roomName?: string): EntityMeta[] {
  return [...list].sort((a, b) => naturalCompare(lightLabel(a, roomName), lightLabel(b, roomName)) || a.id.localeCompare(b.id));
}

/** Fitting type for a light (tile icon before the on/off state pass), so the heading and the icon agree. */
export const lightKind = (m: EntityMeta): string => pickBase(m).icon;

const subgroupCache = new WeakMap<EntityMeta[], Map<string, LightSubgroup[] | null>>();

/**
 * Busy rooms (more than 6 lights) split the light grid under small headings:
 * Ceiling & downlights, Lamps, Accent (strips, mirror, cabinet, wall), Outside,
 * then Other lights for anything the classifier can't place. Returns null for
 * rooms that keep a single grid. Each sub-group is name-sorted (natural order).
 */
export function lightSubgroups(list: EntityMeta[], roomName?: string): LightSubgroup[] | null {
  if (list.length < LIGHT_SUBGROUP_MIN) return null;
  let byRoom = subgroupCache.get(list);
  if (!byRoom) subgroupCache.set(list, (byRoom = new Map()));
  const key = roomName ?? '';
  const hit = byRoom.get(key);
  if (hit !== undefined) return hit;
  const buckets = new Map<LightGroupId, EntityMeta[]>();
  for (const m of list) {
    const g = lightGroupOf(lightKind(m));
    let l = buckets.get(g);
    if (!l) buckets.set(g, (l = []));
    l.push(m);
  }
  const out = LIGHT_GROUPS.filter((g) => buckets.has(g.id)).map((g) => ({ ...g, entities: sortLights(buckets.get(g.id)!, roomName) }));
  // One bucket only (e.g. a stairwell of step lights): headings add nothing.
  const res = out.length > 1 ? out : null;
  byRoom.set(key, res);
  return res;
}
